import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IPatientTreatment } from "../../../../../../@types/Treatments";
import { ReactComponent as NoMessagesImage } from "../../../../../../assets/images/woman-computer.svg";
import { Table } from "../../../../../../components/Table";
import { Tag } from "../../../../../../components/Tag";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  ImageContainer,
  NoContentMessage,
  TableContainer,
} from "./ModalNebulizerTable.styles";
import { nebulizerTableColumns } from "./nebulizerTableColumns";

type ModalNebulizerTableProps = {
  data: IPatientTreatment[];
  isPinnedGraph?: boolean;
  onRowClick?: (treatmentData: IPatientTreatment) => void;
};

export function ModalNebulizerTable({
  data,
  isPinnedGraph,
  onRowClick,
}: ModalNebulizerTableProps) {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <ImageContainer>
        <NoMessagesImage width={181} height={158} />
        <NoContentMessage>
          {t("patientPage_measurementModal_nebulizer_empty")}
        </NoContentMessage>
      </ImageContainer>
    );
  }

  const tableData = useMemo(() => {
    if (data.length === 0) {
      return [];
    }

    const handleCompletionTag = (treatment: IPatientTreatment) => {
      if (treatment.doseStatus === 0) {
        return (
          <Tag key={treatment.id} variant="tertiary">
            {t("patientPage_measurementModal_nebulizer_incomplete")}
          </Tag>
        );
      }

      return (
        <Tag key={treatment.id} variant="success">
          {t("patientPage_measurementModal_nebulizer_complete")}
        </Tag>
      );
    };

    return data
      .sort((a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf())
      .map((treatment) => {
        return {
          id: treatment.id,
          source: "Aerogen device",
          serialNumber: treatment.serialNumber ?? "",
          duration: treatment.totalTreatmentTime,
          date: getLongFormattedDate(treatment.time),
          status: handleCompletionTag(treatment),
        };
      });
  }, [data, t]);

  const handleOnRowClick = (rowContent: any) => {
    if (rowContent.id) {
      const treatmentData = data.find(
        (treatment) => treatment.id === rowContent.id
      );

      if (treatmentData && onRowClick) {
        onRowClick(treatmentData);
      }
    }
  };

  return (
    <TableContainer isPinnedGraph={isPinnedGraph}>
      <Table
        fontWeight={400}
        hasBorder={false}
        data={tableData}
        columns={nebulizerTableColumns}
        onRowClick={handleOnRowClick}
      />
    </TableContainer>
  );
}
